import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Send, CheckCircle2, Loader2, Mail, Pencil } from "lucide-react";

const FILTERS = [
  { k: "all", label: "All" },
  { k: "pending", label: "Pending" },
  { k: "approved", label: "Published" },
];

export function AdminQA({ onChange }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/questions");
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChange?.(); };

  const answer = async (id) => {
    const text = (answers[id] || "").trim();
    if (!text) return toast.error("Write an answer first");
    try {
      await api.put(`/questions/${id}/answer`, { answer: text });
      toast.success(editingId === id ? "Answer updated" : "Answer published");
      setAnswers((a) => ({ ...a, [id]: "" }));
      setEditingId(null);
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const startEdit = (q) => { setEditingId(q.id); setAnswers((a) => ({ ...a, [q.id]: q.answer || "" })); };
  const cancelEdit = (id) => { setEditingId(null); setAnswers((a) => ({ ...a, [id]: "" })); };

  const approve = async (id) => {
    try { await api.put(`/questions/${id}/approve`); toast.success("Published"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/questions/${id}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const filtered = filter === "all" ? items : items.filter((q) => q.status === filter);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-heading font-bold text-3xl tracking-tight text-ink">Q&amp;A Moderation</h1>
        <div className="flex gap-2">
          {FILTERS.map((f) => {
            const count = f.k === "all" ? items.length : items.filter((q) => q.status === f.k).length;
            return (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                data-testid={`qa-filter-${f.k}`}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  filter === f.k ? "bg-clinical text-white border-clinical" : "bg-white border-line text-muted2 hover:border-clinical"
                }`}
              >
                {f.label} {count > 0 && <span className="opacity-70">({count})</span>}
              </button>
            );
          })}
        </div>
      </div>

      {loading && <p className="text-muted2 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}
      {!loading && filtered.length === 0 && <p className="text-muted2">No questions in this view.</p>}

      <div className="grid gap-3" data-testid="qa-admin-list">
        {filtered.map((q) => (
          <div key={q.id} data-testid="qa-admin-row" className="bg-white border border-line rounded-md p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-xs uppercase tracking-wider font-bold text-clinical">{q.announcement_title}</span>
              <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${q.status === "pending" ? "text-alert bg-alert/10" : "text-calm bg-calm/10"}`}>
                {q.status === "pending" ? "Pending" : "Published"}
              </span>
            </div>
            <p className="text-sm font-semibold text-ink mt-2">{q.name}</p>
            <a href={`mailto:${q.email}`} className="text-xs text-muted2 inline-flex items-center gap-1 hover:text-clinical"><Mail className="w-3 h-3" /> {q.email}</a>
            <p className="text-sm text-muted2 mt-2">{q.question}</p>

            {q.answer && editingId !== q.id ? (
              <div className="mt-3 pl-3 border-l-2 border-calm">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-bold uppercase tracking-wider text-calm">{q.answered_by} replied</p>
                  <button onClick={() => startEdit(q)} data-testid="qa-edit-answer-btn" className="inline-flex items-center gap-1 text-xs font-semibold text-clinical hover:text-clinicaldark"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                </div>
                <p className="text-sm text-ink mt-1">{q.answer}</p>
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                <textarea
                  rows={2}
                  placeholder={editingId === q.id ? "Edit your published answer…" : "Write a reply (publishes the question with your answer)…"}
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                  data-testid="qa-answer-input"
                  className="w-full border border-line rounded-md px-3 py-2 text-sm outline-none focus:border-clinical"
                />
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-3">
              {(!q.answer || editingId === q.id) && (
                <button onClick={() => answer(q.id)} data-testid="qa-answer-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white text-sm font-semibold rounded-md px-3 py-1.5 hover:bg-clinicaldark">
                  <Send className="w-4 h-4" /> {editingId === q.id ? "Save answer" : "Answer & publish"}
                </button>
              )}
              {editingId === q.id && (
                <button onClick={() => cancelEdit(q.id)} data-testid="qa-edit-cancel-btn" className="inline-flex items-center gap-1.5 border border-line text-muted2 text-sm font-semibold rounded-md px-3 py-1.5">Cancel</button>
              )}
              {q.status === "pending" && editingId !== q.id && (
                <button onClick={() => approve(q.id)} data-testid="qa-approve-btn" className="inline-flex items-center gap-1.5 border border-calm text-calm text-sm font-semibold rounded-md px-3 py-1.5 hover:bg-calm/5">
                  <CheckCircle2 className="w-4 h-4" /> Publish without reply
                </button>
              )}
              <button onClick={() => remove(q.id)} data-testid="qa-delete-btn" className="inline-flex items-center gap-1.5 text-critical text-sm font-semibold rounded-md px-3 py-1.5 hover:bg-critical/10">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
